import * as React from "react";
import { useCallback, useMemo } from "react";
import { QualifiedFeature } from "fe-common/graphql";
import {
  PanelContext,
  PatientCaseGrouping,
} from "../../context/diagnosticAgent";
import { DataInput } from "../dataInputTypes";
import { LevelPanelItem } from "./common/LevelPanelItem";
import { YesNoPanelItem } from "./common/YesNoPanelItem";
import { DataInputSectionConfigurations } from "./config/dataInputSectionConfiguration";
import { KahunPanelSubGroup } from "shared";
import { FeatureUtil } from "./util/featureUtil";
import { ProcedureItem } from "./common/procedureItem";
import { Suggestion } from "./hooks/useSuggestions";
import { PositiveNegativePanelItem } from "./common/positiveNegativePanelItem";

//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]

export interface EditablePanelItemProps {
  className?: string;
  qualifiedFeature: QualifiedFeature;
  onRefine?: (qf: QualifiedFeature) => void;
  getCaseValue: (
    qf: QualifiedFeature,
    section: PatientCaseGrouping,
    subGroupName?: string,
  ) => DataInput;
  setCaseValue: (
    qf: QualifiedFeature,
    r: DataInput,
    section: PatientCaseGrouping,
    panelContext: PanelContext,
  ) => void;
  panelContext: PanelContext;
  isThreeway?: boolean;
  disabled?: boolean;
  suggestion?: Suggestion;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const EditablePanelItem = (props: EditablePanelItemProps) => {
  const {
    className,
    qualifiedFeature,
    onRefine,
    getCaseValue,
    setCaseValue,
    panelContext,
    isThreeway,
    suggestion,
    disabled,
  } = props;

  const chiefComplaintResponse = useMemo(
    () => getCaseValue(qualifiedFeature, PatientCaseGrouping.MAIN),
    [getCaseValue, qualifiedFeature],
  );

  const dataInput = useMemo(
    () =>
      chiefComplaintResponse ||
      getCaseValue(
        qualifiedFeature,
        panelContext.caseGrouping,
        panelContext.panelCategory?.id,
      ),
    [chiefComplaintResponse, getCaseValue, qualifiedFeature, panelContext],
  );

  const onChange = useCallback(
    (r: DataInput) => {
      setCaseValue(
        qualifiedFeature,
        r,
        chiefComplaintResponse
          ? PatientCaseGrouping.MAIN
          : panelContext.caseGrouping,
        panelContext,
      );
    },
    [setCaseValue, qualifiedFeature, chiefComplaintResponse, panelContext],
  );

  const itemProps = useMemo(() => {
    return {
      className: className,
      qf: qualifiedFeature,
      dataInput,
      onChange,
      isChiefComplaint: !!chiefComplaintResponse,
    };
  }, [
    className,
    qualifiedFeature,
    chiefComplaintResponse,
    dataInput,
    onChange,
  ]);

  const isLabPanel = panelContext.panelCategory?.id === KahunPanelSubGroup.Labs;

  const handleYes = useCallback(() => {
    if (disabled === true) {
      return;
    }

    onChange({ negate: false });
  }, [onChange, disabled]);

  const handleNo = useCallback(() => {
    if (disabled === true) {
      return;
    }

    onChange({ negate: true });
  }, [onChange, disabled]);

  const handleDeselect = useCallback(() => {
    if (disabled === true) {
      return;
    }
    onChange(null);
  }, [onChange, disabled]);

  const handleRefine = useCallback(
    (qf: QualifiedFeature) => {
      onRefine(qf);
    },
    [onRefine],
  );

  // look for section configuration if refinement is allowed
  const isReferentSupported = useMemo(
    () =>
      DataInputSectionConfigurations[panelContext.panelCategory?.id]
        ?.allowRefinement,
    // panelCategory === Cater
    [panelContext],
  );

  const isYesNoSupported = useMemo(
    () =>
      DataInputSectionConfigurations[panelContext.panelCategory?.id]
        ?.allowYesNo,
    // panelCategory === Cater
    [panelContext],
  );

  return FeatureUtil.findingUsesLevel(qualifiedFeature) ? (
    <LevelPanelItem
      className={qualifiedFeature?.id}
      key={qualifiedFeature?.id}
      {...itemProps}
      suggestion={suggestion}
    />
  ) : panelContext.panelCategory?.id === KahunPanelSubGroup.Procedures &&
    !panelContext.fromSearch ? (
    <ProcedureItem
      className={qualifiedFeature?.id}
      key={qualifiedFeature?.id}
      {...itemProps}
      onYes={handleYes}
      onNo={handleNo}
      suggestion={suggestion}
    />
  ) : !isLabPanel ? (
    <YesNoPanelItem
      className={qualifiedFeature?.id}
      key={qualifiedFeature?.id}
      {...itemProps}
      onYes={handleYes}
      labLabels={isLabPanel}
      onNo={handleNo}
      isThreeway={isThreeway}
      onDeselect={handleDeselect}
      allowYesNo={isYesNoSupported}
      onRefine={isReferentSupported ? handleRefine : null}
    />
  ) : (
    <PositiveNegativePanelItem
      className={qualifiedFeature?.id}
      key={qualifiedFeature?.id}
      {...itemProps}
      onYes={handleYes}
      labLabels={isLabPanel}
      onNo={handleNo}
      isThreeway={isThreeway}
      onDeselect={handleDeselect}
      allowYesNo={isYesNoSupported}
      onRefine={isReferentSupported ? handleRefine : null}
    />
  );
};
